import { Component, OnDestroy, OnInit, Renderer2, ViewChild, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
// import { SseService } from '@pflegix/sse';
import { NgClass } from '@angular/common';
import { ProgressBarModule } from 'primeng/progressbar';
import { Subscription, filter } from 'rxjs';
import { LoadingStore } from '../loading.store';
import { MenuService } from './app.menu.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { AppTopbarComponent } from './app.topbar.component';
import { AppConfigComponent } from './config/app.config.component';
import { LayoutService } from './service/app.layout.service';

const BLOCKED_SCROLL_CLASS_NAME = 'blocked-scroll';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-layout',
  templateUrl: './app.layout.component.html',
  styles: `
    .loading-bar {
      position: absolute;
      z-index: 9999;
      top: var(--topbar-height);
      left: 0;
      height: 1rem;
      width: 100%;
    }
  `,
  imports: [NgClass, ProgressBarModule, AppSidebarComponent, AppTopbarComponent, AppConfigComponent, RouterOutlet],
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  private readonly menuService = inject(MenuService);
  public readonly layoutService = inject(LayoutService);
  private readonly renderer = inject(Renderer2);
  private readonly router = inject(Router);

  protected overlayMenuOpenSubscription: Subscription;

  protected topbarMenuOpenSubscription: Subscription;

  protected menuProfileOpenSubscription: Subscription;

  protected menuOutsideClickListener?: (() => void) | null;

  protected menuScrollListener?: (() => void) | null;

  protected topbarMenuOutsideClickListener?: (() => void) | null;

  protected menuProfileOutsideClickListener?: (() => void) | null;

  protected containerClass = computed(() => {
    const layoutConfig = this.layoutService.config();
    const layoutState = this.layoutService.state;

    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'p-input-filled': layoutConfig.inputStyle === 'filled',
      // 'layout-sidebar-dark': layoutConfig.darkTheme,
      'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
      'layout-overlay-active': layoutState.overlayMenuActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-topbar-menu-active': layoutState.topbarMenuActive,
      'layout-menu-profile-active': layoutState.rightMenuActive,
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
      [`layout-topbar-${layoutConfig.topbarTheme}`]: true,
      [`layout-menu-${layoutConfig.menuTheme}`]: true,
      [`layout-menu-profile-${layoutConfig.menuProfilePosition}`]: true,
    };
  });

  @ViewChild(AppSidebarComponent) protected appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopbarComponent) protected appTopbar!: AppTopbarComponent;

  protected loadingStore = inject(LoadingStore);

  constructor() {
    this.hideMenuProfile();

    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      this.hideTopbarMenu();

      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          const isOutsideClicked = !(
            this.appSidebar.el.nativeElement.isSameNode(event.target) ||
            this.appSidebar.el.nativeElement.contains(event.target) ||
            this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
            this.appTopbar.menuButton.nativeElement.contains(event.target)
          );

          if (isOutsideClicked) {
            this.hideMenu();
          }
        });
      }

      if (
        (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus()) &&
        !this.menuScrollListener
      ) {
        this.menuScrollListener = this.renderer.listen(
          this.appSidebar.menuContainer.nativeElement,
          'scroll',
          (event) => {
            if (this.layoutService.isDesktop()) {
              this.hideMenu();
            }
          }
        );
      }

      if (this.layoutService.state.staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.topbarMenuOpenSubscription = this.layoutService.topbarMenuOpen$.subscribe(() => {
      if (!this.topbarMenuOutsideClickListener) {
        this.topbarMenuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          const isOutsideClicked = !(
            this.appTopbar.el.nativeElement.isSameNode(event.target) ||
            this.appTopbar.el.nativeElement.contains(event.target) ||
            this.appTopbar.mobileMenuButton.nativeElement.isSameNode(event.target) ||
            this.appTopbar.mobileMenuButton.nativeElement.contains(event.target)
          );

          if (isOutsideClicked) {
            this.hideTopbarMenu();
          }
        });
      }

      if (this.layoutService.state.staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.menuProfileOpenSubscription = this.layoutService.menuProfileOpen$.subscribe(() => {
      this.hideMenu();

      if (!this.menuProfileOutsideClickListener) {
        this.menuProfileOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          const isOutsideClicked = !(
            this.appSidebar.menuProfile.el.nativeElement.isSameNode(event.target) ||
            this.appSidebar.menuProfile.el.nativeElement.contains(event.target)
          );

          if (isOutsideClicked) {
            this.hideMenuProfile();
          }
        });
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();
      this.hideTopbarMenu();
      this.hideMenuProfile();
    });
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  public ngOnInit(): void {
    // this.sseService.getEvents$().subscribe((event) => {
    //   if (isDevMode()) {
    //     // eslint-disable-next-line no-console
    //     console.log('New Event!', event);
    //   }
    // });
  }

  public ngOnDestroy(): void {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }

    // this.sseService.close();
  }

  protected blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add(BLOCKED_SCROLL_CLASS_NAME);
    } else {
      document.body.className += ` ${BLOCKED_SCROLL_CLASS_NAME}`;
    }
  }

  protected unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove(BLOCKED_SCROLL_CLASS_NAME);
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(`(^|\\b)${BLOCKED_SCROLL_CLASS_NAME.split(' ').join('|')}(\\b|$)`, 'gi'),
        ' '
      );
    }
  }

  protected hideMenu(): void {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    this.menuService.reset();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }
    this.unblockBodyScroll();
  }

  protected hideTopbarMenu(): void {
    this.layoutService.state.topbarMenuActive = false;

    if (this.topbarMenuOutsideClickListener) {
      this.topbarMenuOutsideClickListener();
      this.topbarMenuOutsideClickListener = null;
    }
  }

  protected hideMenuProfile(): void {
    this.layoutService.state.menuProfileActive = false;

    if (this.menuProfileOutsideClickListener) {
      this.menuProfileOutsideClickListener();
      this.menuProfileOutsideClickListener = null;
    }
  }
}
